/**
 * Where a booking the CRM made lives in the dispatch dashboard.
 *
 * Every address is a HASH route — `<origin>/#/job/detail/<id>` — because the
 * dashboard is a Flutter web app on Flutter's default hash URL strategy: its
 * own links are built that way (`job_detail_launchers.dart`, and dispatch-be's
 * password-reset mail), and its `index.html` rewrites a plain path into a
 * fragment for the chauffeur's job page alone. A path URL is not an error, it
 * is worse: the app loads, finds no fragment, and lands on its home page, so
 * the link looks as if it worked and opened the wrong thing.
 *
 * Two routes, because dispatch keeps the two in different tables and the
 * dashboard reaches them differently:
 *
 *   job / quote   `/#/job/detail/<id>` — a real route with the id in the path.
 *   draft         `/#/job/drafts?search=<id>` — there is no per-draft route,
 *                 and the drafts list does not read `search` from its address
 *                 today, so this opens the list and the agent finds the draft
 *                 by its number. The term is kept for the day the list reads
 *                 it. Framed by a dashboard that handles OPEN_JOB, a draft
 *                 opens straight in its booking form instead (JobLink.tsx).
 *
 * The ORIGIN is the interesting part, and its answers must stay in this order.
 * Inside the dashboard the console is framed BY the dashboard, so the parent's
 * own origin is the right answer and cannot be stale. Standalone there is no
 * parent: a local build's `VITE_DASHBOARD_BASE` comes next, and then the
 * tenant's own dashboard as dispatch records it (`GET /meta` → `dashboardUrl`,
 * from `public.tenants.admin_base_url` — the base dispatch builds its own
 * dashboard links on). With none of them there is no link at all — the
 * reference still renders, still reads, still copies, and no agent is sent to a
 * host somebody guessed at.
 */

/** Dashboard ids are numeric everywhere; anything else is not addressable. */
const NUMERIC = /^\d+$/;
const RECORD_ID = /^\d{1,15}$/;

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '');

/** One dashboard route, as an address the browser can open — see the note above. */
const at = (origin: string, route: string) => `${origin}/#${route}`;

/**
 * `document.referrer` is the frame's parent, which for the embedded module is
 * the dashboard. Read as an ORIGIN and never as a URL — the referrer carries a
 * path and a query that are the dashboard's business, not ours.
 *
 * Kept pure (the caller supplies the referrer) so it is testable and so the one
 * place that reads `document` is the hook below.
 */
export function originFromReferrer(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    // A referrer on a non-http scheme is not somewhere a link can go.
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * The dashboard origin to link at, or null when there is nothing trustworthy.
 *
 * @param embedded  true inside the dashboard iframe
 * @param referrer  `document.referrer` — the parent page, when embedded
 * @param configured  the bases to fall back on, best first: a build's
 *                    `VITE_DASHBOARD_BASE`, then the tenant's own dashboard
 *                    (`CrmMeta.dashboardUrl`)
 */
export function dashboardOrigin(
  embedded: boolean,
  referrer: string | null | undefined,
  ...configured: (string | null | undefined)[]
): string | null {
  const parent = embedded ? originFromReferrer(referrer) : null;
  if (parent) return parent;
  for (const candidate of configured) {
    const base = String(candidate ?? '').trim();
    if (base) return stripTrailingSlash(base);
  }
  return null;
}

export type BookingKind = 'draft' | 'job' | 'quote';

/**
 * The link for one booking, or null when it cannot be built — no origin, or a
 * draft whose id is not numeric (see the note at the top). Null is a supported
 * state everywhere it is used: the card renders the reference as text.
 */
export function bookingHref(
  origin: string | null,
  kind: BookingKind,
  dispatchId: string,
): string | null {
  const id = String(dispatchId ?? '').trim();
  if (!origin || !id) return null;
  if (kind === 'draft') {
    return NUMERIC.test(id) ? at(origin, `/job/drafts?search=${encodeURIComponent(id)}`) : null;
  }
  // A quote is a job that has not been booked yet — same table, same route.
  return NUMERIC.test(id) ? at(origin, `/job/detail/${id}`) : null;
}

/** What the link is called, on the card and in the note. */
export function bookingLabel(kind: BookingKind): string {
  return kind === 'draft' ? 'Draft' : kind === 'quote' ? 'Quote' : 'Job';
}

/**
 * Where a PERSON dispatch named lives in the dashboard.
 *
 * Three of the four roles a number can resolve to have a page of their own,
 * each an id in the route (`app_routes.dart`, all `:id(\d+)`, hash-routed like
 * every address here):
 *
 *   customer    `/#/customer/<id>`
 *   affiliate   `/#/affiliates/detail/<id>`
 *   driver      `/#/organisation/driver/<id>` — dispatch's OWN drivers, which is
 *               the table `resolvePhoneIdentity` reads. An affiliate's
 *               chauffeur is a different record with a different route, and
 *               nothing on the wire tells the two apart, so it is not guessed.
 *
 * A passenger has no page at all — dispatch reaches one only through the job
 * that carries them — so it returns null and the name renders as plain text,
 * the same as a missing origin or a missing id.
 *
 * The dashboard also gates each of these on its own permission: an agent
 * without it lands on /no-access rather than the record. That is the
 * dashboard's answer to give, not ours to pre-empt — the CRM cannot read
 * dispatch's per-page permissions, and hiding the link for everyone to spare
 * some of them a redirect helps nobody.
 */
export function profileHref(
  origin: string | null,
  role: string,
  dispatchId: number | string | null | undefined,
): string | null {
  const id = String(dispatchId ?? '').trim();
  if (!origin || !NUMERIC.test(id)) return null;
  if (role === 'customer') return at(origin, `/customer/${id}`);
  if (role === 'affiliate') return at(origin, `/affiliates/detail/${id}`);
  if (role === 'driver') return at(origin, `/organisation/driver/${id}`);
  return null;
}

/**
 * Where one drill-down record lives in the dashboard.
 *
 * The dashboard's own routes, from `app_routes.dart` (hash-routed, as above):
 *
 *   job / quote  `/#/job/detail/<id>`               (the same table, as above)
 *   invoice      `/#/finance/invoice/<id>`
 *   paylog       `/#/finance/pay_logs/detail/<id>`
 *   customer     `/#/customer/<id>`
 *   affiliate    `/#/affiliates/detail/<id>`
 *   driver       `/#/organisation/driver/<id>`
 *
 * Null wherever there is no origin or the id is not numeric, which is a
 * supported state everywhere this is used: the row renders as plain text. The
 * per-page permission is still the dashboard's to answer — an agent without it
 * lands on /no-access, and hiding the link for everyone to spare some of them a
 * redirect helps nobody.
 */
export type RecordTarget = 'job' | 'invoice' | 'customer' | 'affiliate' | 'driver' | 'paylog';

/**
 * Whether a record id names something the dashboard can open: the same rule
 * `recordHref` applies, for a host that opens records itself. A row whose id
 * fails it renders plain on both paths, rather than a button whose message the
 * host is bound to refuse.
 *
 * Fifteen digits at most, as the host reads them (`readCrmDashMessage`): three
 * of its record routes parse the id, and on the web an int past that is a
 * double that names some other record.
 */
export const isRecordId = (id: string | null | undefined): id is string => RECORD_ID.test(String(id ?? '').trim());

export function recordHref(
  origin: string | null,
  target: RecordTarget | null,
  dispatchId: string | null | undefined,
): string | null {
  const id = String(dispatchId ?? '').trim();
  if (!origin || !target || !isRecordId(id)) return null;
  switch (target) {
    case 'job':
      return at(origin, `/job/detail/${id}`);
    case 'invoice':
      return at(origin, `/finance/invoice/${id}`);
    case 'paylog':
      return at(origin, `/finance/pay_logs/detail/${id}`);
    case 'customer':
    case 'affiliate':
    case 'driver':
      return profileHref(origin, target, id);
    default:
      return null;
  }
}

/** A civil day as the board's query takes it. */
const CIVIL_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** A dispatch bucket's code, as `metricMeta.ts` spells every one of them. */
const BUCKET_CODE = /^DISPATCH_[A-Z_]+$/;

/**
 * The dispatch board, opened on what a dispatch card counted: one of its
 * buckets, over the page's window.
 *
 *   `/#/dispatch?bucket=<code>&from=<YYYY-MM-DD>&to=<YYYY-MM-DD>`
 *
 * The board reads `bucket` into its status filter and the two days into its
 * date filter (`DispatchRoute` in `app_routes.dart`). A dashboard build that
 * predates the days still applies the bucket over its own default window,
 * which is a narrower answer and not a wrong one.
 *
 * The standalone console's way there. Framed, the host opens the board itself
 * (`OPEN_DISPATCH`), which keeps the agent in the app they are already in.
 */
export function boardHref(origin: string | null, bucket: string, from: string, to: string): string | null {
  if (!origin || !BUCKET_CODE.test(bucket) || !CIVIL_DAY.test(from) || !CIVIL_DAY.test(to)) return null;
  const query = new URLSearchParams({ bucket, from, to });
  return at(origin, `/dispatch?${query.toString()}`);
}
